import React, { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "../assets/css/Global.css";
import Header from "../components/layout/Header";
import Spinner from "../components/Spinner";
import {
  fetchStudies,
  userByStudy,
  fetchActivities,
  fetchClinics,
  addAuditLogs,
} from "../services/apiServices";
import UserTable from "../components/tables/UserTable";
import { withinRange } from "../utils/dateUtils";
import Footer from "../components/layout/Footer";
import "../assets/css/Study.css";
import { formatDateTimePretty } from "../utils/dateUtils";
import { SiteCards } from "../components/SiteCards";
import { SearchFilter } from "../components/SearchFilter";
import { LuSearchX, LuSearch } from "react-icons/lu";
import { Breadcrumbs } from "../components/Breadcrumbs";
import PaginationBar from "../components/Pagination";
// date helpers moved to utils/dateUtils
const Study = () => {
  const { studyId, siteId, userId } = useParams();
  const navigate = useNavigate();
  const loggedInUser = JSON.parse(localStorage.getItem("user") || "{}");
  // Activities Table Pagination State
  const [uaPage, setUaPage] = useState(1);
  const [uaPageSize, setUaPageSize] = useState(10); // default 10

  const [data, setData] = useState({ sites: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [userActivities, setUserActivities] = useState([]);
  const [filter, setFilter] = useState(false);
  const [uaSearch, setUaSearch] = useState("");
  const [uaFromDate, setUaFromDate] = useState("");
  const [uaToDate, setUaToDate] = useState("");
  const [clinics, setClinics] = useState([]);
  const loadClinics = async () => {
    try {
      const data = await fetchClinics();
      setClinics(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    loadClinics();
  }, []);

  // ---- Fetch Study and Users Data ----
  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        setLoading(true);
        setError("");
        const loggedInUser = JSON.parse(localStorage.getItem("user") || "{}");
        const studies = await fetchStudies();
        const study = Array.isArray(studies)
          ? studies.find((s) => String(s.id) === String(studyId))
          : null;

        const users = await userByStudy(studyId);
        // Helper to parse MySQL tinyint buffer to boolean
        const toBool = (v) => {
          if (typeof v === "boolean") return v;
          if (
            v &&
            typeof v === "object" &&
            v.type === "Buffer" &&
            Array.isArray(v.data)
          ) {
            return v.data[0] !== 0;
          }
          if (typeof v === "number") return v !== 0;
          return !!v;
        };

        // Group users by site (fallback to 'Un-Assigned-Site' if missing)
        const bySite = new Map();
        (Array.isArray(users) ? users : []).forEach((u) => {
          const cname = String(u.clinic_name || "Un-Assigned-Site").trim();
          if (!bySite.has(cname)) bySite.set(cname, []);
          bySite.get(cname).push(u);
        });

        let sites = Array.from(bySite.entries()).map(([siteName, arr]) => ({
          studyName: study?.name || `Study ${studyId}`,
          siteName,
          siteId: siteName, // use site name as identifier in route
          users: arr.map((u) => ({
            id: String(u.id ?? u.userId ?? u.name),
            name: u.name ?? "",
            email: u.email ?? "",
            diaryActive: !!(u.diaryName ?? null),
            active: toBool(u.active),
            lastActivity: u.last_login ?? null,
            avgVoid: u.avg_void ?? null,
            totalVoids: u.total_voids ?? null,
            studyId: String(u.studyId ?? u.study_id ?? studyId),
            siteId: siteName,
            status: u.status ?? "",
            run_result: u.run_result ?? "empty",
            is_test: u.is_test ?? "",
            is_archived: u.is_archived ?? "",
            clinic_name: u.clinic_name ?? "",
            latestActivity: u.latest_activity ?? "No Activity",
            diary_status_label: u.diary_status_label ?? "Not Started",
            today_activity_count: u.today_activity_count ?? 0,
            all_time_activity_count: u.all_time_activity_count ?? 0,
            today_drink_count: u.today_drink_count ?? 0,
            today_pee_count: u.today_pee_count ?? 0,
            today_morning_urination: u.today_morning_urination ?? "No",
            today_before_10am: u.today_before_10am ?? "No",
            today_before_noon: u.today_before_noon ?? "No",
            today_duplicates: u.today_duplicates ?? 0,
            compliance_status: u.compliance_status ?? "no-data",
            compliance_issues: u.compliance_issues ?? "[]"
          })),
        }));

        if (loggedInUser?.source === "user") {
        const allowedSite = (loggedInUser?.clinicName || "").trim();
        sites = sites.filter(
          (s) => String(s.siteName).trim() === allowedSite
        );
      }
        if (isMounted) setData({ sites });
      } catch (e) {
        console.error(e);
        if (isMounted) setError("Failed to load study data");
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, [studyId]);

  // ----- When on a user route, find the selected user and fetch activities -----
  const selectedSite = useMemo(() => {
    if (!siteId) return null;
    const decoded = decodeURIComponent(siteId);
    return data.sites.find((s) => s.siteId === decoded) || null;
  }, [siteId, data]);
  const clinic = clinics.find(clinic => clinic.clinic_name === selectedSite?.siteName);
  const clinicId = clinic ? clinic.id : null;
  const selectedUser = useMemo(() => {
    if (!userId || !selectedSite) return null;
    const uid = decodeURIComponent(userId);
    return selectedSite.users.find((u) => u.id === uid) || null;
  }, [userId, selectedSite]);

  // Get the actual user ID for API calls
  const selectedUserId = useMemo(() => {
    return selectedUser?.id || null;
  }, [selectedUser]);

  useEffect(() => {
    let active = true;
    const loadActivities = async () => {
      if (!selectedUserId) return;
      try {
        setUserActivities([]);
        const raw = await fetchActivities(selectedUserId);
        if (!active) return;
        const parseAttr = (v) => {
          if (v && typeof v === "object") {
            if (Object.prototype.hasOwnProperty.call(v, "S")) return v.S;
            if (Object.prototype.hasOwnProperty.call(v, "N")) {
              const n = Number(v.N);
              return isNaN(n) ? v.N : n;
            }
            if (Object.prototype.hasOwnProperty.call(v, "BOOL"))
              return !!v.BOOL;
          }
          return v;
        };
        const parseItem = (it) => {
          const rawQ = parseAttr(it?.questionnaire);
          let q = {};
          if (rawQ && typeof rawQ === "string") {
            try {
              q = JSON.parse(rawQ);
            } catch (_) {
              q = {};
            }
          }
          const dt = parseAttr(it?.aTime) || null;
          let timeStr = "";
          if (typeof dt === "string") {
            const m = dt.match(
              /T(\d{2}:\d{2}:\d{2})(?:\.\d+)?(Z|[+-]\d{2}:\d{2})/
            );
            if (m) {
              const offset = m[2] === "Z" ? "+00:00" : m[2];
              timeStr = `${m[1]} ${offset}`;
            }
          }
          return {
            id: parseAttr(it?.id),
            date: dt,
            time: timeStr,
            type: parseAttr(it?.type),
            volume: parseAttr(it?.volume),
            intake: parseAttr(it?.intake),
            urgency: parseAttr(it?.urgency),
            leakage: parseAttr(it?.leakage),
            notes: parseAttr(it?.notes),
            device_type: parseAttr(it?.device_type),
            device_measurement: parseAttr(it?.device_measurement),
            first_morning_urination: parseAttr(it?.first_morning_urination),
            night_pee: parseAttr(it?.night_pee),
            automode: parseAttr(it?.automode),
            utc_offset: parseAttr(it?.utc_offset),
            userid: parseAttr(it?.userid),
            questionnaire: {
              quit: !!q.quit,
              ql: !!q.ql,
              qutv: !!q.qutv,
              qas: !!q.qas,
              qlm: q.qlm || "",
              qlr: q.qlr || "",
              qum: q.qum || "",
            },
          };
        };
        const list = Array.isArray(raw) ? raw : [raw];
        const normalized = list.map(parseItem);
        setUserActivities(normalized);
      } catch (e) {
        console.error(e);
      } finally {
        if (active) setLoading(false);
      }
    };
    loadActivities();
    return () => {
      active = false;
    };
  }, [selectedUserId]);

  // Filter user activities
  const uaFiltered = useMemo(() => {
    setLoading(true)
    const q = uaSearch.toLowerCase();
    return userActivities.filter((a) => {
      const text = `
      ${a.id ?? ""}
      ${a.type ?? ""}
      ${a.notes ?? ""}
      ${a.device_type ?? ""}
      ${a.questionnaire?.qutv ? "Urge To Void" : ""}
      ${a.questionnaire?.qas ? "After Sleep" : ""}
      ${a.questionnaire?.qlm ?? ""}
      ${a.questionnaire?.qlr ?? ""}
      ${a.questionnaire?.qum ?? ""}
    `.toLowerCase();
      const textMatch = !q || text.includes(q);
      const dateMatch = withinRange(a.date, uaFromDate, uaToDate);
      setLoading(false)
      return textMatch && dateMatch;
    }).sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [userActivities, uaSearch, uaFromDate, uaToDate]);
  const uaPageCount = Math.ceil(uaFiltered.length / uaPageSize);

  const uaPaged = uaFiltered.slice(
    (uaPage - 1) * uaPageSize,
    uaPage * uaPageSize
  );


  const exportUserCsv = async () => {
    const csvRows = [
      [
        "Activity Date Time",
        "Pee (ml)",
        "Drink (oz)",
        "Urine In Toilet",
        "Leak",
        "Magnitude Of Leak",
        "Leak Reason",
        "Urgency",
        "Urgency Type",
        "Notes",
      ],
      ...uaFiltered.map((a) => {
        const peeMl = a.type === "Output" ? a.volume ?? "" : "";
        const drinkOz =
          a.type === "Intake" && a.intake != null
            ? (Number(a.intake) / 29.5735).toFixed(1)
            : "";
        const urineInToilet = a.questionnaire?.quit ? "Yes" : "No";
        const leak = a.questionnaire?.ql ? "Yes" : "No";
        const leakMag = a.questionnaire?.qlm || "";
        const leakReason = a.questionnaire?.qlr || "";
        const urgencyVal = a.questionnaire?.qum || (a.urgency ?? "");
        const types = [];
        if (a.questionnaire?.qutv) types.push("Urge To Void");
        if (a.questionnaire?.qas) types.push("After Sleep");
        const urgencyType = types.join(", ");
        const notes = (a.notes ?? "").toString().replace(/\n/g, " ");
        return [
          a.date ? formatDateTimePretty(a.date, a.utc_offset) : "",
          peeMl,
          drinkOz,
          urineInToilet,
          leak,
          leakMag,
          leakReason,
          urgencyVal,
          urgencyType,
          notes,
        ];
      }),
    ];
    const csv = csvRows
      .map((row) =>
        row
          .map((c) => {
            const s = String(c ?? "");
            return s.includes(",") ? `"${s.replace(/"/g, '""')}"` : s;
          })
          .join(",")
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${selectedUser?.email || "user"}_activities.csv`;
    a.click();
    URL.revokeObjectURL(url);
    const res = await addAuditLogs({ new_data: { filename: `${selectedUser?.email || "user"}_activities.csv`, selectedUser: selectedUser?.email }, action_type: "csv download" });

  };

  // Flatten users for KPIs
  const handleUserUpdate = (updatedUser) => {
    setData((prevData) => ({
      ...prevData,
      sites: prevData.sites.map((site) => ({
        ...site,
        users: site.users.map((u) =>
          u.id === updatedUser.id ? { ...u, ...updatedUser } : u
        ),
      })),
    }));
  };

  return (
    <div className="container">
      <Header
        actions={
          <>
            {loggedInUser?.source !== "user" && (
              <button
                className="button secondary"
                onClick={() => navigate("/clinic")}
              >
                All Clinics
              </button>
            )}
            <button
              className="button secondary"
              onClick={() => navigate("/home")}
            >
              All Studies
            </button>
          </>
        }
      />

      {loading && (
        <div className="spinner-center" style={{ minHeight: "600px" }}>
          <Spinner label="Loading study data" />
        </div>
      )}
      {error && (
        <div className="panel" style={{ marginTop: 16, color: "red" }}>
          {error}
        </div>
      )}

      {!loading && !error && data.sites.length === 0 && (
        <div
          className="empty-state-panel"
          style={{
            textAlign: "center",
            padding: "40px 20px",
            margin: "20px 0",
          }}
        >
          {/* <h3 style={{ marginBottom: "10px", color: "#555" }}>
            Not Found
          </h3> */}
          <p style={{ color: "#7a7676ff", marginBottom: "20px", fontSize: "14px" }}>
           Currently, There is no clinic assign to user with this email. Please contact admin to assign clinic to this account
          </p>
          <button className="button primary" onClick={() => navigate("/home")}>
            Back to Home
          </button>
        </div>
      )}

      {!loading && !error && data.sites.length > 0 && (
        <>
          <Breadcrumbs
            clinicId={clinicId}
            studyName={data.sites[0]?.studyName || "Study"}
            siteName={selectedSite?.siteName}
            userLabel={selectedUser?.name || selectedUser?.id}
            isSiteView={!!siteId && !userId}
            isUserView={!!userId}
            onStudyClick={() => navigate(`/study/${studyId}`)}
            // onStudyClick={() => navigate("/home")}
            onSiteClick={() =>
              navigate(`/study/${studyId}/${encodeURIComponent(selectedSite?.siteId)}`)
            }
          />
          <SiteCards
            data={data}
            siteId={siteId}
            studyId={studyId}
          />
          {siteId && !userId && selectedSite && (
            <div className="panel">
              {selectedSite.users.length === 0 ? (
                <div className="empty-state">No users found for this site.</div>
              ) : (
                <UserTable
                  title={`Subjects : ${selectedSite.siteName}`}
                  rows={selectedSite.users.map((u) => ({
                    site: selectedSite.siteName,
                    user: u,
                  }))}
                  onExportFileName={`site_${selectedSite.siteName}_users.csv`}
                  selectedSitename={selectedSite.siteName}
                  emptyMessage="No users found"
                  onUserUpdate={handleUserUpdate}
                />
                // <Site site={selectedSite}/>
              )}
            </div>
          )}
          {siteId && userId && selectedUser && (
            <div className="panel">
              <div className="table-header">
                <div className="header-left">
                  <span><strong>Subject Activities: {selectedUser.name}</strong></span>
                  <span className="results-count">
                    Total Activities: {uaFiltered.length}
                  </span>

                  <div className="search-container">
                    <button
                      className="filter-icon"
                      onClick={() => setFilter((v) => !v)}
                    >
                      {filter ? <LuSearchX /> : <LuSearch />}
                    </button>
                    <div className={`search-filter-inline ${filter ? "open" : ""}`}>
                      <SearchFilter
                        filter={filter}
                        search={uaSearch}
                        onSearchChange={setUaSearch}
                        onClear={() => {
                          setUaSearch("");
                          setUaFromDate("");
                          setUaToDate("");
                        }}
                        checkboxes={[]}
                        page={uaPage}
                        setPage={setUaPage}
                        pageCount={uaPageCount}
                        pageSize={uaPageSize}
                        setPageSize={setUaPageSize}
                        totalCount={uaFiltered.length}
                      />
                    </div>
                  </div>
                </div>

                <div className="actions">
                  <button
                    className="button secondary"
                    onClick={() =>
                      navigate(`/study/${studyId}/${encodeURIComponent(selectedSite.siteId)}`)
                    }
                  >
                    Back to Site
                  </button>
                  <button className="button primary" onClick={exportUserCsv}>
                    Download CSV
                  </button>
                </div>
              </div>

              <div className="table-wrapper">
                {loading ? (
                  <div className="spinner-center" style={{ minHeight: "300px" }}>
                    <Spinner label="Loading activities..." />
                  </div>
                ) : uaPaged.length === 0 ? (
                  <div className="empty-state">
                    <h4 style={{ margin: 0 }}>No Activities Found</h4>
                    <p style={{ fontSize: "13px", margin: 0 }}>Try changing search or date filters</p>
                  </div>
                ) : (

                  <table className="data-table activities-table">
                    <thead>
                      <tr>
                        <th>Activity Date Time</th>
                        <th>Pee (ml)</th>
                        <th>Drink (oz)</th>
                        <th>Urine In Toilet</th>
                        <th>Leak</th>
                        <th>Magnitude Of Leak</th>
                        <th>Leak Reason</th>
                        <th>Urgency</th>
                        <th>Urgency Type</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {uaPaged.map((a, idx) => (
                        <tr key={a.id || idx}>
                          <td data-label="Activity Date Time">{formatDateTimePretty(a.date, a.utc_offset)}</td>
                          <td data-label="Pee (ml)">{a.type === "Output" ? a.volume ?? "—" : "—"}</td>
                          <td data-label="Drink (oz)">{a.type === "Intake" ? (Number(a.intake) / 29.5735).toFixed(1) : "—"}</td>
                          <td data-label="Urine In Toilet">{a.questionnaire?.quit ? "Yes" : "No"}</td>
                          <td data-label="Leak">{a.questionnaire?.ql ? "Yes" : "No"}</td>
                          <td data-label="Magnitude Of Leak">{a.questionnaire?.qlm || "—"}</td>
                          <td data-label="Leak Reason">{a.questionnaire?.qlr || "—"}</td>
                          <td data-label="Urgency">{a.questionnaire?.qum || a.urgency || "—"}</td>
                          <td data-label="Urgency Type">{[a.questionnaire?.qutv && "Urge To Void", a.questionnaire?.qas && "After Sleep"].filter(Boolean).join(", ") || "—"}</td>
                          <td data-label="Notes"> {a.notes || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                <PaginationBar
                  page={uaPage}
                  pageCount={uaPageCount}
                  onPageChange={setUaPage}
                  pageSize={uaPageSize}
                  onPageSizeChange={setUaPageSize}
                  totalCount={uaFiltered.length}
                />
              </div>
            </div>

          )}
        </>
      )}
      <Footer />
    </div>
  );
};
export default Study;
